import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Mail, User, Phone, MapPin, Search, RefreshCw, Loader2, Trash2,
  UserPlus, ExternalLink, Calendar, AlertCircle, CheckCircle, MessageSquare, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DuplicateBadge } from '../components/DuplicateBadge';
import { LeadSendEmailDialog } from '../components/LeadSendEmailDialog';
import { BulkEmailLeadsDialog } from '../components/BulkEmailLeadsDialog';
import { Checkbox } from '../components/ui/checkbox';

const formatDateTime = (d) => {
  if (!d) return '-';
  const date = new Date(d);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const EmailLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);
  const [emailDialogLead, setEmailDialogLead] = useState(null);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [messageDialog, setMessageDialog] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [convertDialog, setConvertDialog] = useState(false);
  const [converting, setConverting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === leads.length && leads.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map((l) => l.id)));
    }
  };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/email-leads', {
        params: { skip: page * 50, limit: 50, search: searchQuery, filter },
      });
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load email leads');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, filter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/email-leads/sync');
      toast.success(res.data.message);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleConvert = async () => {
    if (!selectedLead) return;
    setConverting(true);
    try {
      const res = await api.post(`/email-leads/${selectedLead.id}/convert`);
      toast.success('Lead converted to customer');
      setConvertDialog(false);
      setSelectedLead(null);
      navigate(`/customers/${res.data.customer_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this email lead?')) return;
    try {
      await api.delete(`/email-leads/${id}`);
      toast.success('Lead deleted');
      fetchLeads();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const newCount = leads.filter(l => !l.converted).length;
  const convertedCount = leads.filter(l => l.converted).length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">Email Leads</h1>
            <p className="text-sm text-slate-500 mt-1">Emails from internal mailbox — noreply@helioses.co.uk</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-blue-100 text-blue-700 px-3 py-1">
              <Mail className="w-3 h-3 mr-1" />
              {total} Total
            </Badge>
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="sync-email-leads-btn"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync Emails
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, subject..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              className="pl-10"
              data-testid="search-email-leads"
            />
          </div>
          <div className="flex gap-1">
            {['all', 'new', 'converted'].map(f => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setFilter(f); setPage(0); }}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? 'All' : f === 'new' ? 'New' : 'Converted'}
              </Button>
            ))}
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <Card className="border-blue-300 bg-blue-50">
            <CardContent className="py-3 flex items-center justify-between">
              <span className="text-sm font-medium">
                {selectedIds.size} lead{selectedIds.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setBulkEmailOpen(true)}
                  data-testid="bulk-email-btn"
                >
                  <Mail className="w-3 h-3 mr-1" /> Email Selected
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
              </div>
            ) : leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Inbox className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">{searchQuery ? 'No matching email leads' : 'No email leads yet. Click "Sync Emails" to pull from mailbox.'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={leads.length > 0 && selectedIds.size === leads.length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="font-semibold">Sender</TableHead>
                    <TableHead className="font-semibold">Subject</TableHead>
                    <TableHead className="font-semibold">Phone</TableHead>
                    <TableHead className="font-semibold">Postcode</TableHead>
                    <TableHead className="font-semibold">Received</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="w-[120px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map(lead => (
                    <TableRow
                      key={lead.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setMessageDialog(lead)}
                      data-testid={`email-lead-row-${lead.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(lead.id)}
                          onCheckedChange={() => toggleSelect(lead.id)}
                          data-testid={`select-lead-${lead.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                            <Mail className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{lead.sender_name || lead.sender_email}</p>
                            <p className="text-xs text-slate-500">{lead.sender_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-700 truncate max-w-[300px]">{lead.subject}</p>
                      </TableCell>
                      <TableCell>
                        {lead.phone ? (
                          <span className="flex items-center gap-1 text-sm text-slate-600">
                            <Phone className="w-3 h-3" /> {lead.phone}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {lead.postcode ? (
                          <span className="text-sm text-slate-600">{lead.postcode}</span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDateTime(lead.received_at)}</TableCell>
                      <TableCell>
                        <Badge className={lead.converted
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                          : 'bg-blue-100 text-blue-700 border-blue-200'
                        }>
                          {lead.converted ? 'Converted' : 'New'}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {lead.sender_email && (
                            lead.email_log?.length > 0 ? (
                              <Button
                                size="sm"
                                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                                onClick={() => setEmailDialogLead({ ...lead, email: lead.sender_email, name: lead.sender_name })}
                                data-testid={`email-btn-${lead.id}`}
                                title={`${lead.email_log.length} email(s) sent — click to reply`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Email Sent{lead.email_log.length > 1 ? ` (${lead.email_log.length})` : ''}
                              </Button>
                            ) : (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-slate-500 hover:text-blue-600"
                                onClick={() => setEmailDialogLead({ ...lead, email: lead.sender_email, name: lead.sender_name })}
                                data-testid={`email-btn-${lead.id}`}
                                title="Reply"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            )
                          )}
                          {!lead.converted ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-8 text-xs"
                              onClick={() => { setSelectedLead(lead); setConvertDialog(true); }}
                              data-testid={`convert-btn-${lead.id}`}
                            >
                              <UserPlus className="w-3 h-3 mr-1" /> Create Customer
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs"
                              onClick={() => navigate(`/customers/${lead.customer_id}`)}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" /> View
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(lead.id)}
                            data-testid={`delete-email-lead-${lead.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > 50 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {page * 50 + 1} - {Math.min((page + 1) * 50, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* View Email Dialog */}
      <Dialog open={!!messageDialog} onOpenChange={() => setMessageDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              Email Lead
            </DialogTitle>
            <DialogDescription>{messageDialog?.subject}</DialogDescription>
          </DialogHeader>
          {messageDialog && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium">{messageDialog.sender_name}</p>
                  <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      <a href={`mailto:${messageDialog.sender_email}`} className="text-amber-600 hover:underline">{messageDialog.sender_email}</a>
                    </span>
                    {messageDialog.phone && (
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {messageDialog.phone}</span>
                    )}
                    {messageDialog.postcode && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {messageDialog.postcode}</span>
                    )}
                  </div>
                </div>
              </div>

              {messageDialog.address && (
                <div className="text-sm">
                  <span className="text-slate-500 text-xs font-medium">Address:</span>
                  <p className="text-slate-700">{messageDialog.address}</p>
                </div>
              )}

              <div>
                <span className="text-slate-500 text-xs font-medium">Full Message:</span>
                <ScrollArea className="max-h-80 mt-1">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="email-message-content">
                      {messageDialog.body_text || 'No content'}
                    </p>
                  </div>
                </ScrollArea>
              </div>

              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Calendar className="w-3 h-3" /> Received: {formatDateTime(messageDialog.received_at)}
              </div>
            </div>
          )}
          <DialogFooter>
            {!messageDialog?.converted && (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => { setSelectedLead(messageDialog); setMessageDialog(null); setConvertDialog(true); }}
                data-testid="dialog-convert-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Create Customer
              </Button>
            )}
            {messageDialog?.converted && (
              <Button variant="outline" onClick={() => { setMessageDialog(null); navigate(`/customers/${messageDialog.customer_id}`); }}>
                <ExternalLink className="w-4 h-4 mr-2" /> View Customer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={convertDialog} onOpenChange={setConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Convert Email Lead to Customer
            </DialogTitle>
            <DialogDescription>Create a new customer record from this email lead.</DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">{selectedLead.sender_name || selectedLead.sender_email}</p>
                    <p className="text-sm text-slate-500">Email Lead</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Email:</span><p className="font-medium">{selectedLead.email || '-'}</p></div>
                  <div><span className="text-slate-500">Phone:</span><p className="font-medium">{selectedLead.phone || '-'}</p></div>
                  {selectedLead.address && (
                    <div className="col-span-2"><span className="text-slate-500">Address:</span><p className="font-medium">{selectedLead.address}</p></div>
                  )}
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-500 text-xs font-medium">Subject (will be saved in notes):</span>
                <p className="text-sm text-slate-700 mt-1 line-clamp-3">{selectedLead.subject}</p>
              </div>
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>A new customer will be created with status "Lead". The email content will be saved in notes.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialog(false)}>Cancel</Button>
            <Button onClick={handleConvert} disabled={converting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-convert-btn">
              {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeadSendEmailDialog
        open={!!emailDialogLead}
        onOpenChange={(v) => !v && setEmailDialogLead(null)}
        lead={emailDialogLead}
        leadSource="email"
      />

      <BulkEmailLeadsDialog
        open={bulkEmailOpen}
        onOpenChange={setBulkEmailOpen}
        selectedLeads={leads
          .filter((l) => selectedIds.has(l.id))
          .map((l) => {
            const name = l.sender_name || '';
            const parts = name.split(' ');
            return {
              id: l.id,
              email: l.sender_email,
              name,
              first_name: l.first_name || parts[0] || '',
              last_name: l.last_name || parts.slice(1).join(' ') || '',
              phone: l.phone,
            };
          })}
        leadSource="email"
        onComplete={fetchLeads}
      />
    </Layout>
  );
};

export default EmailLeads;
